import { LeagueNavigatorItem } from '@/components/league-navigator/components/LeagueNavigator';
import { SPECIAL_TABS } from '@/components/league-navigator/utils/league-navigator-utils';
import { Sport } from '@/feature/event-details-sbk/types';

import { FeaturedLeague } from '../components/FeaturedLeagueSection';
import { NavigationEntry, NavigationLeagueEntry } from '../hooks/use-navigation-entries';
import { competitionTileId, liveId, lobbyId, sportTileId } from '../hooks/use-sbk-lobby-section-selection';

export const promosId = 'promos';

export type LaunchpadItem = LeagueNavigatorItem &
    ({ kind: 'explore' | 'promos' | 'live' } | { kind: NavigationEntry['type']; entryId: string });

export type LaunchpadLabels = {
    explore: string;
    promos: string;
    live: string;
};

// the response is unvalidated JSON, so an entry type this build does not know is dropped rather than rendered without an id
const getEntryIds = (entry: NavigationEntry) => {
    switch (entry.type) {
        case 'league':
            return { entryId: entry.league_id, id: entry.league_id };
        case 'sport':
            return { entryId: entry.sport_id, id: sportTileId(entry.sport_id) };
        case 'competition':
            return { entryId: entry.id, id: competitionTileId(entry.id) };
        default:
            return undefined;
    }
};

export const buildLaunchpadItems = (
    entries: NavigationEntry[],
    liveHubEnabled: boolean,
    labels: LaunchpadLabels
): LaunchpadItem[] => {
    const hasLiveEvents = entries.some(entry => entry.live_event_count > 0);
    const live: LaunchpadItem = { kind: 'live', id: liveId, label: labels.live, ...SPECIAL_TABS.live };

    return [
        { kind: 'explore', id: lobbyId, label: labels.explore, ...SPECIAL_TABS.explore },
        { kind: 'promos', id: promosId, label: labels.promos, ...SPECIAL_TABS.promos },
        ...(liveHubEnabled && hasLiveEvents ? [live] : []),
        ...entries.flatMap((entry): LaunchpadItem[] => {
            const ids = getEntryIds(entry);
            return ids
                ? [
                      {
                          kind: entry.type,
                          ...ids,
                          label: entry.label,
                          image: entry.icon_url ? { uri: entry.icon_url } : undefined,
                      },
                  ]
                : [];
        }),
    ];
};

export const findEntryByTileId = (entries: NavigationEntry[], tileId: string) =>
    entries.find(entry => getEntryIds(entry)?.id === tileId);

export const findEntryForLeague = (entries: NavigationEntry[], leagueId: string) => {
    const leagueEntry = entries.find(entry => entry.type === 'league' && entry.league_id === leagueId);
    if (leagueEntry) {
        return { entry: leagueEntry, subGroup: undefined };
    }

    for (const entry of entries) {
        const subGroup = entry.sub_groups.find(group => group.type === 'league' && group.league_id === leagueId);
        if (subGroup) {
            return { entry, subGroup };
        }
    }

    return undefined;
};

export const getFeaturedLeagues = (entries: NavigationEntry[]): FeaturedLeague[] =>
    entries
        .filter((entry): entry is NavigationLeagueEntry => entry.type === 'league' && entry.is_featured)
        .map(entry => ({
            id: entry.league_id,
            name: entry.label,
            imageUrl: entry.icon_url ?? undefined,
            eventCount: entry.event_display_count,
        }));

export const findNavigationSport = (entries: NavigationEntry[], leagueId: string): Sport | undefined =>
    entries.find(
        entry =>
            (entry.type === 'league' && entry.league_id === leagueId) ||
            entry.sub_groups.some(subGroup => subGroup.league_id === leagueId)
    )?.sport;
